import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, LogOut, GripVertical } from 'lucide-react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { uploadCourse } from '@/services/importApi';
import FileUpload from '@/components/import/FileUpload';
import ImportStep1Metadata from '@/pages/import/ImportStep1Metadata';
import ImportStep2Goals from '@/pages/import/ImportStep2Goals';
import ImportStep3Activities from '@/pages/import/ImportStep3Activities';
import Step4Finish from '@/pages/Step4Finish';
import ProgressBar from '@/components/ProgressBar';
import ChatPanelEmbedded from '@/components/ChatPanelEmbedded';
import { useToast } from '@/hooks/use-toast';
import { CourseProvider, useCourse } from '@/contexts/CourseContext';
import { useAuth } from '@/contexts/AuthContext';
import { STORAGE_KEYS } from '@/constants';
import { getStepHeaderInfo } from '@/lib/stepHeaders';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ensureNumberValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  const cleaned = `${value}`.replace(/,/g, '');
  const match = cleaned.match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const extractCourseInfoFields = (courseInfo = {}) => ({
  title: courseInfo.Title || courseInfo.title || courseInfo.name || '',
  description: courseInfo.Description || courseInfo.description || '',
  subjectArea:
    courseInfo.SubjectArea ||
    courseInfo.subjectarea ||
    courseInfo.subjectArea ||
    courseInfo.subject ||
    '',
  educationalLevel:
    courseInfo.EducationalLevel ||
    courseInfo.level ||
    courseInfo.Level ||
    courseInfo.gradeLevel ||
    '',
  grade: courseInfo.Grade || courseInfo.grade || courseInfo.class || '',
  totalHours:
    ensureNumberValue(courseInfo.TotalHours) ||
    ensureNumberValue(courseInfo.totalHours) ||
    ensureNumberValue(courseInfo.Duration) ||
    ensureNumberValue(courseInfo.duration) ||
    ensureNumberValue(courseInfo.Hours) ||
    0
});

const resolveObjectiveId = (objectiveText, objectives) => {
  if (!objectives || objectives.length === 0) {
    return null;
  }

  if (!objectiveText || typeof objectiveText !== 'string') {
    return objectives[0].id;
  }

  const normalizedObjectiveText = objectiveText.trim().toLowerCase();
  const match = objectives.find((objective) => {
    const text = (objective?.text || '').toLowerCase();
    return (
      (text && normalizedObjectiveText && text.includes(normalizedObjectiveText)) ||
      (normalizedObjectiveText && text && normalizedObjectiveText.includes(text))
    );
  });

  return match ? match.id : objectives[0].id;
};

const collectGoalStrings = (input) => {
  if (!input) return [];
  const list = Array.isArray(input) ? input : [input];
  return list
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object') {
        return entry.text || entry.title || entry.description || entry.value || '';
      }
      return '';
    })
    .map((text) => (text ? text.trim() : ''))
    .filter(Boolean);
};

const normalizeImportedTopics = (rawTopics = [], goals = []) => {
  if (!Array.isArray(rawTopics)) return [];

  return rawTopics.map((topic, topicIdx) => {
    const topicId =
      topic.id ||
      topic.TopicId ||
      topic.topicId ||
      `${topic.TopicName || topic.title || 'topic'}-${topicIdx + 1}`;
    const title =
      topic.TopicName ||
      topic.topicName ||
      topic.title ||
      topic.name ||
      `Topic ${topicIdx + 1}`;

    const rawActivities = Array.isArray(topic.Activities)
      ? topic.Activities
      : Array.isArray(topic.activities)
        ? topic.activities
        : [];

    const activities = rawActivities.map((activity, actIdx) => {
      const activityId =
        activity.id ||
        activity.Id ||
        activity.activityId ||
        `${topicId}-activity-${actIdx + 1}`;

      // Support both new (objectiveId) and legacy (goalId) field names
      const objectiveReference =
        Array.isArray(activity.Goals) && activity.Goals.length > 0
          ? activity.Goals[0]
          : Array.isArray(activity.goals) && activity.goals.length > 0
            ? activity.goals[0]
            : activity.Goal ||
            activity.goal ||
            activity.goalText ||
            activity.goal_id ||
            activity.objectiveId ||
            activity.goalId ||
            '';

      const goalTexts = collectGoalStrings(
        Array.isArray(activity.Goals) && activity.Goals.length > 0
          ? activity.Goals
          : Array.isArray(activity.goals) && activity.goals.length > 0
            ? activity.goals
            : objectiveReference
              ? [objectiveReference]
              : []
      );

      const durationValue =
        ensureNumberValue(activity.Duration) ||
        ensureNumberValue(activity.duration) ||
        ensureNumberValue(activity.durationMinutes) ||
        ensureNumberValue(activity.estimatedMinutes);

      return {
        id: activityId,
        title:
          activity.Title || activity.title || activity.name || `Activity ${actIdx + 1}`,
        description: activity.Description || activity.description || '',
        actor: activity.Actor || activity.actor || activity.collaboration || 'student',
        facilitatorRole:
          activity.FacilitatorRole || activity.facilitatorRole || activity.facilitator || '',
        bloomLevel:
          activity.BloomType || activity.bloomLevel || activity.BloomLevel || 'Understanding',
        resourceType:
          activity.ResourceType || activity.resourceType || activity.resource || '',
        resourceTitle:
          activity.ResourceTitle ||
          activity.resourceTitle ||
          activity.title ||
          activity.Resource ||
          '',
        resourceDescription:
          activity.ResourceDescription || activity.resourceDescription || activity.description || '',
        objectiveId: resolveObjectiveId(objectiveReference, goals),
        goals: goalTexts,
        duration: durationValue > 0 ? durationValue : undefined
      };
    });

    return {
      id: topicId,
      title,
      activities
    };
  });
};


/**
 * Import Course Content (wrapped by CourseProvider)
 */
function ImportCourseContent({ onBack, chatLanguage = 'English' }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentStep, dispatch } = useCourse();
  const { user, logout } = useAuth();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [customChatHandler, setCustomChatHandler] = useState(null);

  // Data state
  const [uploadResult, setUploadResult] = useState(null);
  const [courseData, setCourseData] = useState({});
  const [importedRawTopics, setImportedRawTopics] = useState([]);

  const loadImportSessionSummary = async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await axios.get(`${API_URL}/import/session/${sessionId}`);
      const session = response.data.session || response.data.course_data || {};
      const normalizedData = session.normalizedData || session.rawData || {};
      const rawTopics = normalizedData.Topics || normalizedData.topics || [];
      setImportedRawTopics(rawTopics);
      const metadata = extractCourseInfoFields(
        normalizedData.CourseInfo || normalizedData.courseInfo || {}
      );
      const initialGoals = session.lockedGoals || [];
      const normalizedTopics = normalizeImportedTopics(rawTopics, initialGoals);
      setCourseData((prev) => ({
        ...prev,
        ...metadata,
        topics: normalizedTopics
      }));
    } catch (error) {
      console.error('Error loading import session summary:', error);
    }
  };

  // CRITICAL FIX: Always start at Step 0 (upload screen) on fresh mount
  // Clear any stale session data and reset to upload screen
  useLayoutEffect(() => {
    // Always clear previous session and start fresh
    localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
    localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);
    dispatch({ type: 'SET_STEP', payload: 0 });

    // Cleanup on unmount - clear session to ensure fresh start next time
    return () => {
      localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
      localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);
    };
  }, [dispatch]);

  // NOTE: Session restore logic removed - Import mode now always starts fresh at Step 0 (upload screen)
  // The session is saved during the flow (handleFileUpload) and cleared on unmount

  // Load course data when reaching Step 4 (handles reload scenario)
  useEffect(() => {
    const loadCourseDataForStep4 = async () => {
      // Only load if we're at Step 4 and have a courseId but no courseData
      if (currentStep === 4 && uploadResult?.courseId && (!courseData || !courseData.topics)) {
        try {
          const response = await axios.get(`${API_URL}/courses/${uploadResult.courseId}`);
          const fullCourseData = response.data;

          setCourseData((prev) => ({
            ...prev,
            ...fullCourseData,
            topics: fullCourseData.topics || prev.topics || [],
            goals: fullCourseData.goals || prev.goals || []
          }));
        } catch (error) {
          console.error('Error loading course data for Step 4:', error);
                  toast({
                    variant: 'destructive',
                    title: t('importCourse.error'),
                    description: t('importCourse.failedToLoad'),
                  });
        }
      }
    };

    loadCourseDataForStep4();
  }, [currentStep, uploadResult?.courseId, courseData, toast]);

  // Handle file upload
  const handleFileUpload = async (file) => {
    setIsLoading(true);
    try {
      const result = await uploadCourse(file);

      setUploadResult(result);
      const uploadedCourseData = result.courseData || {};

      // Set the chat language (interactionLanguage) from the selected language
      // This ensures the AI assistant chats in the user's chosen language
      uploadedCourseData.interactionLanguage = chatLanguage;

      setCourseData(uploadedCourseData);
      // Update global course context so ProgressBar can check hasCourseInfo
      dispatch({ type: 'SET_COURSE', payload: uploadedCourseData });
      dispatch({ type: 'SET_STEP', payload: 1 }); // Move to Step 1: Metadata
      dispatch({ type: 'SET_IMPORT_MODE', payload: { mode: true, sessionId: result.importSessionId } });

      // Store import flow IDs in separate storage keys
      if (result.courseId) {
        localStorage.setItem(STORAGE_KEYS.IMPORT_COURSE_ID, result.courseId);

        // Save the interactionLanguage to the backend
        try {
          await axios.patch(`${API_URL}/courses/${result.courseId}`, {
            interactionLanguage: chatLanguage
          });
          console.log(`Saved interactionLanguage (${chatLanguage}) to course ${result.courseId}`);
        } catch (langError) {
          console.warn('Failed to save interaction language:', langError);
          // Don't fail the upload if language save fails
        }
      }
      if (result.importSessionId) {
        localStorage.setItem(STORAGE_KEYS.IMPORT_SESSION_ID, result.importSessionId);
      }
      // Clear scratch flow storage to prevent contamination
      localStorage.removeItem(STORAGE_KEYS.COURSE_ID);

      await loadImportSessionSummary(result.importSessionId);

      toast({
        title: 'Επιτυχία!',
        description: `Το μάθημα ανέβηκε επιτυχώς. ${result.topicsCount} θέματα, ${result.activitiesCount} δραστηριότητες βρέθηκαν.`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Αποτυχία Ανεβάσματος',
        description: error.response?.data?.detail || 'Αποτυχία ανεβάσματος μαθήματος. Παρακαλώ ελέγξτε τη μορφή του αρχείου.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle completion of Step 1 (Metadata)
  const handleMetadataComplete = async (data) => {
    if (data.metadata) {
      setCourseData(prev => ({ ...prev, ...data.metadata }));
    }

    // Fetch fresh course data to get goals
    try {
      const courseId = uploadResult?.courseId;
      if (courseId) {
        const freshCourse = await axios.get(`${API_URL}/courses/${courseId}`);
        if (freshCourse.data) {
          setCourseData(freshCourse.data);
          console.log('Fetched fresh course data for Step 2:', freshCourse.data);
        }
      }
    } catch (error) {
      console.error('Error fetching fresh course data:', error);
    }

    dispatch({ type: 'SET_STEP', payload: 2 }); // Move to Step 2: Goals

    toast({
      title: 'Μεταδεδομένα Επιβεβαιώθηκαν!',
      description: 'Προχωρούμε στον έλεγχο των στόχων.',
    });
  };

  // Handle completion of Step 2 (Goals)
  const handleGoalsComplete = async (data) => {
    // First update with passed data
    if (data.goals) {
      setCourseData(prev => ({ ...prev, goals: data.goals }));
    }

    // Fetch fresh course data to get latest goals and topics
    try {
      const courseId = uploadResult?.courseId;
      if (courseId) {
        const freshCourse = await axios.get(`${API_URL}/courses/${courseId}`);
        if (freshCourse.data) {
          setCourseData(freshCourse.data);
          console.log('Fetched fresh course data for Step 3:', freshCourse.data);
        }
      }
    } catch (error) {
      console.error('Error fetching fresh course data:', error);
    }

    dispatch({ type: 'SET_STEP', payload: 3 }); // Move to Step 3: Activities

    toast({
      title: 'Στόχοι Επιβεβαιώθηκαν!',
      description: 'Προχωρούμε στον σχεδιασμό των δραστηριοτήτων.',
    });
  };

  // Handle completion of Step 3 (Activities) - final step
  const handleActivitiesComplete = async (data) => {
    // Update uploadResult with courseId if provided in response
    if (data.courseId && (!uploadResult?.courseId || uploadResult.courseId !== data.courseId)) {
      setUploadResult((prev) => ({
        ...prev,
        courseId: data.courseId
      }));
      // Store courseId in localStorage
      localStorage.setItem(STORAGE_KEYS.IMPORT_COURSE_ID, data.courseId);
      console.log(`Stored courseId ${data.courseId} from Step 3 completion`);
    }

    // Determine which courseId to use (from response or existing uploadResult)
    const courseIdToUse = data.courseId || uploadResult?.courseId;

    // Load complete course data from backend before moving to Step 4
    if (courseIdToUse) {
      try {
        const response = await axios.get(`${API_URL}/courses/${courseIdToUse}`);
        const fullCourseData = response.data;

        // Update global CourseContext so Step 4 (and ProgressBar) see the latest activities/goals
        dispatch({ type: 'SET_COURSE', payload: fullCourseData });

        // Update local courseData with complete data from backend
        setCourseData((prev) => ({
          ...prev,
          ...fullCourseData,
          // Ensure topics are properly structured
          topics: fullCourseData.topics || prev.topics || [],
          // Ensure goals are properly structured
          goals: fullCourseData.goals || prev.goals || []
        }));
      } catch (error) {
        console.error('Error loading course data for Step 4:', error);
        toast({
          variant: 'destructive',
          title: t('importCourse.error'),
          description: t('importCourse.failedToLoadRefresh'),
        });
        return;
      }
    } else {
      // No courseId available - log warning but proceed (data might be in courseData already)
      console.warn('No courseId available when completing Step 3 - proceeding with existing courseData');
    }

    dispatch({ type: 'SET_STEP', payload: 4 }); // Move to finish step

    toast({
      title: t('importCourse.completed'),
      description: t('importCourse.courseImproved'),
    });
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="max-w-3xl mx-auto">
            <FileUpload onFileUpload={handleFileUpload} isLoading={isLoading} />
          </div>
        );
      case 1:
        return (
          <ImportStep1Metadata
            courseId={uploadResult?.courseId}
            importSessionId={uploadResult?.importSessionId}
            initialCourseData={courseData}
            onComplete={handleMetadataComplete}
            setCustomChatHandler={setCustomChatHandler}
            updateCourseData={async (updates) => {
              setCourseData((prev) => {
                if (updates && typeof updates === 'object' && (updates.topics || updates.goals || updates.id)) {
                  return { ...updates };
                }
                return { ...prev, ...updates };
              });
              if (uploadResult?.courseId) {
                try {
                  await axios.patch(`${API_URL}/courses/${uploadResult.courseId}`, updates);
                } catch (error) {
                  console.error('Error updating course:', error);
                  toast({
                    variant: 'destructive',
                    title: t('importCourse.error'),
                    description: t('importCourse.failedToSave'),
                  });
                }
              }
            }}
          />
        );
      case 2:
        return (
          <ImportStep2Goals
            courseId={uploadResult?.courseId}
            importSessionId={uploadResult?.importSessionId}
            initialCourseData={courseData}
            onComplete={handleGoalsComplete}
            setCustomChatHandler={setCustomChatHandler}
            updateCourseData={async (updates) => {
              setCourseData((prev) => {
                if (updates && typeof updates === 'object' && (updates.topics || updates.goals || updates.id)) {
                  return { ...updates };
                }
                return { ...prev, ...updates };
              });
              if (uploadResult?.courseId) {
                try {
                  await axios.patch(`${API_URL}/courses/${uploadResult.courseId}`, updates);
                } catch (error) {
                  console.error('Error updating course:', error);
                  toast({
                    variant: 'destructive',
                    title: t('importCourse.error'),
                    description: t('importCourse.failedToSave'),
                  });
                }
              }
            }}
          />
        );
      case 3:
        return (
          <ImportStep3Activities
            courseId={uploadResult?.courseId}
            importSessionId={uploadResult?.importSessionId}
            courseData={courseData}
            onComplete={handleActivitiesComplete}
            setCustomChatHandler={setCustomChatHandler}
            updateCourseData={async (updates) => {
              setCourseData((prev) => {
                if (updates && typeof updates === 'object' && (updates.topics || updates.goals || updates.id)) {
                  return { ...updates };
                }
                return { ...prev, ...updates };
              });
              if (uploadResult?.courseId) {
                try {
                  await axios.patch(`${API_URL}/courses/${uploadResult.courseId}`, updates);
                } catch (error) {
                  console.error('Error updating course:', error);
                  toast({
                    variant: 'destructive',
                    title: t('importCourse.error'),
                    description: t('importCourse.failedToSave'),
                  });
                }
              }
            }}
          />
        );
      case 4:
        return (
          <Step4Finish
            courseData={courseData}
            courseId={uploadResult?.courseId}
            // In import flow we always want the freshest data from CourseContext,
            // which is updated right after Step 3 completes.
            preferContextData={true}
            updateCourseData={async (updates) => {
              // Update local state - handle both partial updates and full course objects
              setCourseData((prev) => {
                // If updates is a full course object (has id, topics, goals, etc.), use it directly
                // Otherwise, merge with previous state
                if (updates && typeof updates === 'object' && (updates.topics || updates.goals || updates.id)) {
                  return { ...updates };
                }
                return { ...prev, ...updates };
              });

              // Update backend if courseId exists
              if (uploadResult?.courseId) {
                try {
                  // If updates is a full course object, send it all
                  // Otherwise, send just the updates
                  await axios.patch(`${API_URL}/courses/${uploadResult.courseId}`, updates);
                } catch (error) {
                  console.error('Error updating course:', error);
                  toast({
                    variant: 'destructive',
                    title: t('importCourse.error'),
                    description: t('importCourse.failedToSave'),
                  });
                }
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  const showChatPanel = currentStep >= 1 && currentStep <= 3;

  // Import-specific restart handler
  const handleImportRestart = async () => {
    // Clear import storage keys
    localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
    localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.COURSE_ID); // Safety clear
    
    // Reset state
    dispatch({ type: 'SET_STEP', payload: 0 });
    dispatch({ type: 'SET_COURSE', payload: null });
    dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
    dispatch({ type: 'CLEAR_IMPORT_MODE' });
    
    // Reset local state
    setUploadResult(null);
    setCourseData({});
    setImportedRawTopics([]);
    setCustomChatHandler(null);
  };

  // Upload view (step 0)
  if (currentStep === 0) {
    return (
      <div className="h-screen bg-gradient-to-br from-orange-100 via-pink-50 to-teal-100 py-6 px-6 overflow-hidden">
        <div className="max-w-[1800px] mx-auto h-full flex flex-col">
          {/* Header */}
          <div className="mb-4 flex items-start justify-between flex-shrink-0">
            <div className="flex-1">
              <button
                onClick={() => {
                  // Clear session data when explicitly going back/exiting
                  localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
                  localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);
                  localStorage.removeItem(STORAGE_KEYS.COURSE_ID); // Safety clear
                  onBack();
                }}
                className="flex items-center text-gray-700 hover:text-gray-900 mb-3 font-medium transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('importCourse.backToHome')}
              </button>
              <h1 className="text-3xl font-extrabold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent">
                {t('importCourse.importImproveTitle')}
              </h1>
              <p className="text-gray-600 mt-2 text-sm">
                {t('importCourse.importDescription')}
              </p>
            </div>
            {user && (
              <div className="flex items-start gap-3">
                <div className="text-right mr-2">
                  <p className="text-sm text-gray-600">{t('common.loggedInAs')}</p>
                  <p className="text-sm font-semibold text-gray-800">{user.username}</p>
                </div>
                <button
                  onClick={logout}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                  title={t('common.logout')}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>

          {/* File Upload - Positioned towards top */}
          <div className="flex-1 flex items-start justify-center pt-8">
            {renderStepContent()}
          </div>
        </div>
      </div>
    );
  }

  // Steps 1-4: Use same layout as start-from-scratch
  return (
    <div className="h-screen bg-gradient-to-br from-orange-100 via-pink-50 to-teal-100 py-6 px-6 overflow-hidden">
      <div className="max-w-[1800px] mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <button
                onClick={() => {
                  // Clear session data when explicitly going back/exiting
                  localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
                  localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);
                  localStorage.removeItem(STORAGE_KEYS.COURSE_ID); // Safety clear
                  onBack();
                }}
                className="flex items-center text-gray-700 hover:text-gray-900 mb-2 font-medium transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('importCourse.backToHome')}
              </button>
              {currentStep === 0 ? (
                // Step 0 (upload view) - keep static header
                <>
                  <h1 className="text-3xl font-extrabold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent">
                    {t('importCourse.importImproveTitle')}
                  </h1>
                  <p className="text-gray-600 mt-1 text-sm mb-2">
                    Βελτιώστε το μάθημά σας βήμα-βήμα με τη βοήθεια του AI
                  </p>
                </>
              ) : (
                // Steps 1-4 - use dynamic step header
                (() => {
                  const stepHeaderInfo = getStepHeaderInfo(currentStep, true, t);
                  const IconComponent = stepHeaderInfo.icon;
                  // Calculate stats for Step 4
                  const totalGoals = currentStep === 4 ? (courseData?.learningObjectives?.length || courseData?.goals?.length || 0) : 0;
                  const totalTopics = currentStep === 4 ? (courseData?.topics?.length || 0) : 0;
                  const totalActivities = currentStep === 4 ? ((courseData?.topics || []).reduce((sum, t) => sum + (t.activities?.length || 0), 0)) : 0;
                  return (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <IconComponent className={stepHeaderInfo.iconColor} size={36} />
                        <h1 className="text-3xl font-extrabold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent">
                          {stepHeaderInfo.title}
                        </h1>
                      </div>
                      {stepHeaderInfo.subtitle && (
                        <p className="text-gray-600 mt-1 text-sm mb-2">
                          {stepHeaderInfo.subtitle}
                        </p>
                      )}
                      {currentStep === 4 && (
                        <div className="flex items-center gap-2 mt-2 mb-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{t('step4.learningObjectives')}: <span className="font-bold">{totalGoals}</span></span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{t('step4.topics')}: <span className="font-bold">{totalTopics}</span></span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{t('step4.activities')}: <span className="font-bold">{totalActivities}</span></span>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
            {user && (
              <div className="flex items-start gap-3">
                <div className="text-right mr-2">
                  <p className="text-sm text-gray-600">{t('common.loggedInAs')}</p>
                  <p className="text-sm font-semibold text-gray-800">{user.username}</p>
                </div>
                <button
                  onClick={logout}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                  title={t('common.logout')}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
          {/* Progress Bar in its own centered row */}
          <ProgressBar />
        </div>

        {/* Step Content with Chat Panel */}
        <div className="flex-1 min-h-0">
        {currentStep === 4 ? (
          <div className="h-full overflow-y-auto">
            {renderStepContent()}
          </div>
        ) : (
          <PanelGroup direction="horizontal" className="h-full">
            <Panel defaultSize={50} minSize={25}>
              <div className="h-full overflow-y-auto pr-[1px]">
                {renderStepContent()}
              </div>
            </Panel>
            {showChatPanel && (
              <>
                <PanelResizeHandle className="w-4 mx-1 flex items-center justify-center group cursor-col-resize">
                  <div className="w-1.5 h-20 bg-gray-300 rounded-full group-hover:bg-orange-400 group-active:bg-orange-500 transition-colors flex items-center justify-center">
                    <GripVertical className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
                  </div>
                </PanelResizeHandle>
                <Panel defaultSize={50} minSize={30} className="h-full">
                  <ChatPanelEmbedded onSendMessage={customChatHandler} />
                </Panel>
              </>
            )}
          </PanelGroup>
        )}
        </div>
      </div>
    </div>
  );
}

/**
 * Import Course (with CourseProvider wrapper)
 */
const ImportCourse = ({ onBack, chatLanguage = 'English' }) => {
  return (
    <CourseProvider>
      <ImportCourseContent onBack={onBack} chatLanguage={chatLanguage} />
    </CourseProvider>
  );
};

export default ImportCourse;
